import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Chart } from "primereact/chart";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import jwt_decode from "jwt-decode";
import axios from "../../../utils/axios";
import { logout } from "../../../utils/msal-utils";
import { MIDDLE_WARE_BASE_URL_v1, DOWNLOAD_INDIA_COMPLIANCE_REPORT, DOWNLOAD_COMPLIANCE_PDF, HOME } from "../../../utils/CONSTANTS";
import ttecLogo from "../../../images/TTEC Standalone Logo - White.svg";
import {
  fetchIndiaComplianceDashboard,
  checkComplianceDashboardAccess,
  indiaComplianceDashboardDetails,
} from "./getIndiaComplianceDashboardSlice";

/* Brand palette (matches the approved mockup) */
const C = {
  navy: "#012A57", navy2: "#01407A", cyan: "#00C2FF", cyan2: "#0495FF",
  green: "#00B87A", amber: "#F59E0B", rose: "#F43F5E", gray: "#A0A0B8",
  border: "#DDE6F0", text: "#0B1F3A", muted: "#5A7294", bg: "#F8FAFF",
};

const STATUS_OPTIONS = ["Completed", "In Progress", "Overdue", "Not Started"].map((s) => ({ label: s, value: s }));

type Row = {
  oracleId: string; name: string; isFemale: boolean;
  program: string; trainer: string; classRosterId: string; wave: string; location: string;
  startDate: string; dueDate: string; completedOn: string;
  forms: { form2: string; gratuity: string; form11: string; formL: string };
  completionPct: number; overall: string;
};

const STATUS_TAG: Record<string, { sev: any; color: string }> = {
  Completed: { sev: "success", color: C.green },
  "In Progress": { sev: "warning", color: C.amber },
  Overdue: { sev: "danger", color: C.rose },
  "Not Started": { sev: null, color: C.gray },
};

export default function IndiaComplianceDashboard() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [showActions, setShowActions] = useState(false);
  const userMenu = useRef<Menu>(null);
  const initials = useMemo(() => {
    try {
      const raw = localStorage.getItem("IdToken") || (localStorage.getItem("AccessToken") || "").replace(/^Bearer\s+/i, "");
      const c: any = raw ? jwt_decode(raw) : {};
      const fn = c.given_name || c.firstName || c.first_name || "";
      const ln = c.family_name || c.lastName || c.last_name || "";
      if (fn && ln) return (fn[0] + ln[0]).toUpperCase();
      const name = (c.name || "").trim();
      if (name) { const p = name.split(/\s+/); return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase(); }
      const email = c.email || localStorage.getItem("email") || "";
      const parts = (email.split("@")[0] || "").split(/[._-]/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
    } catch { /* ignore */ }
    return "U";
  }, []);
  const { dashboardData, hasAccess, accessState } = useSelector(indiaComplianceDashboardDetails);

  // query state (server-side)
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(15);
  const [sortBy, setSortBy] = useState("startDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [status, setStatus] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [formLPendingOnly, setFormLPendingOnly] = useState(false); // client-side view over the page (v1)
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [program, setProgram] = useState<string | null>(null);
  const [trainer, setTrainer] = useState<string | null>(null);
  const [classRoster, setClassRoster] = useState<string | null>(null);
  const [dueFrom, setDueFrom] = useState<Date | null>(null);
  const [dueTo, setDueTo] = useState<Date | null>(null);
  const [submitCount, setSubmitCount] = useState(0);
  const filtersRef = useRef<any>({});
  filtersRef.current = { status, search, program, trainer, classRoster, dateFrom, dateTo, dueFrom, dueTo };
  const [reportFrom, setReportFrom] = useState<Date | null>(null);
  const [reportTo, setReportTo] = useState<Date | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [selected, setSelected] = useState<Row[]>([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const MAX_REPORT_DAYS = 92;
  const addDays = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const onReportFromChange = (d: Date | null) => {
    setReportFrom(d);
    if (d && reportTo && reportTo > addDays(d, MAX_REPORT_DAYS)) setReportTo(null);
  };
  const onReportToChange = (d: Date | null) => {
    setReportTo(d);
    if (d && reportFrom && reportFrom < addDays(d, -MAX_REPORT_DAYS)) setReportFrom(null);
  };
  const load = useCallback(() => {
    if (!hasAccess) return; // allowlist gate
    const f = (d: Date | null) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : undefined;
    const fl = filtersRef.current;
    dispatch(fetchIndiaComplianceDashboard({
      page, pageSize: rows, sortBy, sortDir, search: fl.search,
      status: fl.status,
      dateFrom: f(fl.dateFrom), dateTo: f(fl.dateTo),
      program: fl.program, trainer: fl.trainer, classRosterId: fl.classRoster,
      dueFrom: f(fl.dueFrom), dueTo: f(fl.dueTo),
    }));
  }, [dispatch, page, rows, sortBy, sortDir, hasAccess, submitCount]);

  // allowlist access check on mount
  useEffect(() => {
    dispatch(checkComplianceDashboardAccess({
      oracleId: localStorage.getItem("oracleId") || "",
      email: localStorage.getItem("email") || "",
    }));
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const summary = dashboardData?.summary ?? { total: 0, completed: 0, inProgress: 0, overdue: 0, notStarted: 0, pct: 0, femaleTotal: 0, formLCompleted: 0, formLPending: 0, form2Done: 0, gratuityDone: 0, form11Done: 0 };
  const totalRows: number = dashboardData?.totalRows ?? 0;
  const pageRows: Row[] = dashboardData?.rows ?? [];
  const displayRows = useMemo(
    () => (formLPendingOnly ? pageRows.filter((r) => r.isFemale && r.forms.formL !== "done") : pageRows),
    [pageRows, formLPendingOnly]
  );

  /* ---------- chart data (from real summary counts) ---------- */
  const statusChartData = useMemo(() => ({
    labels: ["Completed", "In Progress", "Overdue", "Not Started"],
    datasets: [{ data: [summary.completed, summary.inProgress, summary.overdue, summary.notStarted], backgroundColor: [C.green, C.amber, C.rose, "#94A3B8"], borderWidth: 0, hoverOffset: 4 }],
  }), [summary]);
  const donutOptions = useMemo(() => ({ cutout: "66%", maintainAspectRatio: false, plugins: { legend: { position: "bottom" as const, labels: { boxWidth: 12, font: { size: 11 }, color: C.text } } } }), []);
  const complianceChartData = useMemo(() => ({
    labels: ["Compliant", "Remaining"],
    datasets: [{ data: [summary.pct, Math.max(0, 100 - summary.pct)], backgroundColor: [C.cyan2, "#E6EDF7"], borderWidth: 0 }],
  }), [summary]);
  const ringOptions = useMemo(() => ({ cutout: "78%", maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } } }), []);

  const programOpts = useMemo(() => ((dashboardData?.filterOptions?.programs ?? []) as string[]).map((p) => ({ label: p, value: p })), [dashboardData]);
  const trainerOpts = useMemo(() => ((dashboardData?.filterOptions?.trainers ?? []) as string[]).map((t) => ({ label: t, value: t })), [dashboardData]);
  const classOpts = useMemo(() => ((dashboardData?.filterOptions?.classes ?? []) as string[]).map((c) => ({ label: c, value: c })), [dashboardData]);

  const onPage = (e: any) => { setRows(e.rows); setPage((e.page ?? 0) + 1); };
  const onSort = (e: any) => {
    const map: Record<string, string> = { name: "name", oracleId: "oracleId", startDate: "startDate", dueDate: "dueDate", overall: "overall" };
    setSortBy(map[e.sortField] ?? "startDate");
    setSortDir(e.sortOrder === 1 ? "asc" : "desc");
  };
  const clearAll = () => { setStatus(null); setSearch(""); setFormLPendingOnly(false); setDateFrom(null); setDateTo(null); setProgram(null); setTrainer(null); setClassRoster(null); setDueFrom(null); setDueTo(null); setSortBy("startDate"); setSortDir("desc"); setPage(1); setSubmitCount((c) => c + 1); };
  const submitFilters = () => { setPage(1); setSubmitCount((c) => c + 1); };

  const downloadPdf = async (r: Row) => {
    try {
      const qs = new URLSearchParams({
        oracleId: r.oracleId,
        viewerOracleId: localStorage.getItem("oracleId") || "",
        viewerEmail: localStorage.getItem("email") || "",
      });
      const url = MIDDLE_WARE_BASE_URL_v1() + DOWNLOAD_COMPLIANCE_PDF() + "?" + qs.toString();
      const res: any = await axios.getBlobRequest(url);
      const safeName = r.name.replace(/[^a-zA-Z0-9 ]/g, "").trim().replace(/\s+/g, "_");
      const fileName = `Compliance_${safeName}_${r.oracleId}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (e: any) {
      const s = e?.response?.status;
      toast.current?.show({
        severity: s === 404 || s === 403 ? "warn" : "error",
        summary: "PDF",
        detail: s === 404 ? "No submitted data for this employee."
          : s === 403 ? "Not authorized to download this PDF."
            : "PDF download failed. Try again.",
        life: 3000,
      });
    }
  };

  const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const downloadReport = async (from?: Date | null, to?: Date | null) => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.append("date_from", fmtDate(from));
      if (to) qs.append("date_to", fmtDate(to));
      const url = MIDDLE_WARE_BASE_URL_v1() + DOWNLOAD_INDIA_COMPLIANCE_REPORT() + (qs.toString() ? `?${qs.toString()}` : "");
      const res: any = await axios.getBlobRequest(url);
      const cd: string = res?.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const fileName = m ? decodeURIComponent(m[1]) : `India_Compliance_${fmtDate(new Date())}.xlsx`;
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      toast.current?.show({ severity: "success", summary: "Report", detail: "Download started.", life: 2500 });
    } catch (e: any) {
      const status = e?.response?.status;
      toast.current?.show({
        severity: status === 404 ? "warn" : "error",
        summary: "Report",
        detail: status === 404 ? "No new hires found for that period." : "Report download failed. Try again.",
        life: 3000,
      });
    } finally {
      setDownloading(false);
    }
  };

  const downloadBulk = async () => {
    const targets = (selected || []).slice(0, 5);
    if (!targets.length) return;
    setBulkDownloading(true);
    try {
      for (const r of targets) {
        const qs = new URLSearchParams({ oracleId: r.oracleId, viewerOracleId: localStorage.getItem("oracleId") || "", viewerEmail: localStorage.getItem("email") || "" });
        const url = MIDDLE_WARE_BASE_URL_v1() + DOWNLOAD_COMPLIANCE_PDF() + "?" + qs.toString();
        const res: any = await axios.getBlobRequest(url);
        const safeName = r.name.replace(/[^a-zA-Z0-9 ]/g, "").trim().replace(/\s+/g, "_");
        const fileName = `Compliance_${safeName}_${r.oracleId}.pdf`;
        const blob: Blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
        await new Promise((r2) => setTimeout(r2, 400));
      }
      toast.current?.show({ severity: "success", summary: "PDFs", detail: "Downloaded " + targets.length + " PDF(s).", life: 2500 });
      setSelected([]);
    } catch (e: any) {
      toast.current?.show({ severity: "error", summary: "PDFs", detail: "Bulk download failed. Try again.", life: 3000 });
    } finally {
      setBulkDownloading(false);
    }
  };

  /* ---------- cell templates ---------- */
  const nameTpl = (r: Row) => (
    <span style={{ fontWeight: 500 }}>{r.name}{r.isFemale && <span style={{ color: "#9b59b6", marginLeft: 4 }} title="Female (Form L applicable)">&#9792;</span>}</span>
  );
  const formTpl = (v: string) => {
    if (v === "na") return <span style={{ color: C.gray, fontSize: 12, fontWeight: 600 }}>N/A</span>;
    const m: Record<string, [string, string]> = { done: [C.green, "\u2713"], pending: [C.amber, "\u22EF"], missing: [C.rose, "\u2717"] };
    const [color, icon] = m[v] ?? [C.gray, "\u2014"];
    return <span style={{ color, fontWeight: 700 }}>{icon}</span>;
  };
  const statusTpl = (r: Row) => {
    const s = STATUS_TAG[r.overall] ?? STATUS_TAG["Not Started"];
    return <Tag value={r.overall} severity={s.sev} style={!s.sev ? { background: "#EEF0F5", color: "#5A5A78" } : undefined} rounded />;
  };
  const actionTpl = (r: Row) => (
    <Button icon="pi pi-download" label="PDF" className="p-button-sm p-button-outlined" disabled={r.completionPct !== 100} tooltip={r.completionPct !== 100 ? "Forms not submitted yet" : undefined} onClick={() => downloadPdf(r)} />
  );

  const detailItem = (label: string, node: React.ReactNode) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10.5, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: C.text, overflowWrap: "anywhere" }}>{node}</div>
    </div>
  );
  const rowExpansionTemplate = (r: Row) => (
    <div style={{ padding: "14px 18px", background: "#F8FAFF", whiteSpace: "normal" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16 }}>
        {detailItem("Program", <span title={r.program}>{r.program || "\u2014"}</span>)}
        {detailItem("Trainer", r.trainer || "\u2014")}
        {detailItem("Class", r.classRosterId || "\u2014")}
        {detailItem("Form 2", formTpl(r.forms.form2))}
        {detailItem("Gratuity", formTpl(r.forms.gratuity))}
        {detailItem("Form 11", formTpl(r.forms.form11))}
        {detailItem("Form L", formTpl(r.forms.formL))}
      </div>
    </div>
  );

  /* ---------- KPI card ---------- */
  const kpi = (label: string, value: any, accent: string, meta?: string) => (
    <div style={{ ...cardBox, borderLeft: `3px solid ${accent}` }}>
      <div style={kLabel}>{label}</div>
      <div style={{ ...kValue, color: accent === C.cyan2 ? C.text : accent }}>{value}</div>
      {meta && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{meta}</div>}
    </div>
  );

  if (accessState !== "fulfilled") {
    return <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Checking access…</div>;
  }
  if (!hasAccess) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: C.muted }}>
        <i className="pi pi-lock" style={{ fontSize: 28, color: C.rose }} />
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>You don't have access to this dashboard.</div>
        <div style={{ fontSize: 13 }}>Contact the compliance team if you believe this is an error.</div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text }}>
      <Toast ref={toast} />
      <style>{`.icd-actions-mask { backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); background: rgba(1,42,87,.28) !important; }`}</style>

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 60%, #015099 100%)`, padding: "18px 0" }}>
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 44px", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", position: "relative" }}>
          <img src={ttecLogo} alt="TTEConnect home" title="Go to TTEConnect home" onClick={() => navigate(HOME)} style={{ height: 30, cursor: "pointer", display: "block", zIndex: 1 }} />
          <div style={{ position: "absolute", left: 0, right: 0, textAlign: "center", pointerEvents: "none" }}>
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>India Compliance Dashboard</div>
            <div style={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>Onboarding Digital Form &middot; Statutory Forms Tracking</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, zIndex: 1 }}>
            <Button label="Export Report" icon="pi pi-download" className="p-button-sm" onClick={() => setShowActions(true)} style={{ background: "#00C2FF", border: "none", color: "#012A57", fontWeight: 700 }} />
            <div
              onMouseEnter={(e) => userMenu.current?.show(e)}
              title="Account"
              style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.45)", color: "#fff", fontWeight: 700, fontSize: 15, letterSpacing: ".5px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              {initials}
            </div>
            <Menu popup ref={userMenu} model={[
              { label: "Refresh", icon: "pi pi-refresh", command: () => load() },
              { label: "Sign out", icon: "pi pi-sign-out", command: () => logout() },
            ]} />
          </div>
        </div>

        <Dialog header="Compliance Report" visible={showActions} onHide={() => setShowActions(false)} modal dismissableMask maskClassName="icd-actions-mask" style={{ width: "min(560px, 94vw)" }} breakpoints={{ "640px": "96vw" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ fontSize: 13, color: C.muted }}>Same Excel that's emailed weekly &mdash; download on demand.</div>
            <Button label="Download last 7 days" icon="pi pi-download" loading={downloading} onClick={() => downloadReport()} />
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Custom Range</div>
              <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>From</span>
                  <Calendar
                    value={reportFrom ?? undefined}
                    onChange={(e: any) => onReportFromChange(e.value)}
                    dateFormat="yy-mm-dd" placeholder="Start date" showIcon
                    minDate={reportTo ? addDays(reportTo, -MAX_REPORT_DAYS) : undefined}
                    maxDate={reportTo || undefined}
                    style={{ width: "100%" }} inputStyle={{ width: "100%" }}
                  />                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>To</span>
                  <Calendar
                    value={reportTo ?? undefined}
                    onChange={(e: any) => onReportToChange(e.value)}
                    dateFormat="yy-mm-dd" placeholder="End date" showIcon
                    minDate={reportFrom || undefined}
                    maxDate={reportFrom ? addDays(reportFrom, MAX_REPORT_DAYS) : undefined}
                    style={{ width: "100%" }} inputStyle={{ width: "100%" }}
                  />
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: -4, marginBottom: 8 }}>Max 92-day range</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <Button label="Generate & Download" icon="pi pi-download" className="p-button-outlined" loading={downloading} disabled={!reportFrom || !reportTo} onClick={() => downloadReport(reportFrom, reportTo)} />
              </div>
            </div>
          </div>
        </Dialog>
      </div>

      <div style={{ maxWidth: 1320, margin: "0 auto", padding: "20px 28px 56px" }}>
        {/* Filters */}
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10, marginLeft: 2 }}>Filters</div>
        <div className="icd-filters" style={{ ...cardBox, padding: "16px 18px", marginBottom: 12 }}>
          <style>{`
            .icd-filters .p-inputtext { height: 38px; padding-top: 0; padding-bottom: 0; font-size: 13px; }
            .icd-filters .p-dropdown { height: 38px; }
            .icd-filters .p-dropdown .p-dropdown-label { display: flex; align-items: center; padding-top: 0; padding-bottom: 0; font-size: 13px; }
            .icd-filters .p-calendar { height: 38px; }
            .icd-filters .p-datepicker-trigger { height: 38px; }
          `}</style>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
            <Dropdown value={status} options={STATUS_OPTIONS} onChange={(e) => setStatus(e.value)} placeholder="All Statuses" showClear style={{ width: 190 }} />
            <span className="p-input-icon-left" style={{ width: 360, maxWidth: "100%" }}>
              <i className="pi pi-search" />
              <InputText value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitFilters(); }} placeholder="Search name / oracle id" style={{ width: "100%" }} />
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 12 }}>
            <Dropdown value={program} options={programOpts} onChange={(e) => setProgram(e.value)} placeholder="Program" showClear filter style={{ width: "100%" }} />
            <Dropdown value={trainer} options={trainerOpts} onChange={(e) => setTrainer(e.value)} placeholder="Trainer" showClear filter style={{ width: "100%" }} />
            <Dropdown value={classRoster} options={classOpts} onChange={(e) => setClassRoster(e.value)} placeholder="Class" showClear filter style={{ width: "100%" }} />
            <Calendar value={dateFrom ?? undefined} onChange={(e: any) => setDateFrom(e.value)} dateFormat="yy-mm-dd" placeholder="Start from" showIcon style={{ width: "100%" }} inputStyle={{ width: "100%" }} />
            <Calendar value={dateTo ?? undefined} onChange={(e: any) => setDateTo(e.value)} dateFormat="yy-mm-dd" placeholder="Start to" minDate={dateFrom || undefined} showIcon style={{ width: "100%" }} inputStyle={{ width: "100%" }} />
            <Calendar value={dueFrom ?? undefined} onChange={(e: any) => setDueFrom(e.value)} dateFormat="yy-mm-dd" placeholder="Due from" showIcon style={{ width: "100%" }} inputStyle={{ width: "100%" }} />
            <Calendar value={dueTo ?? undefined} onChange={(e: any) => setDueTo(e.value)} dateFormat="yy-mm-dd" placeholder="Due to" minDate={dueFrom || undefined} showIcon style={{ width: "100%" }} inputStyle={{ width: "100%" }} />
          </div>
        </div>

        {/* Filter actions — below the box, right corner */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <Button label="Clear all" className="p-button-text p-button-sm" onClick={clearAll} />
          <Button label="Submit" className="p-button-sm" onClick={submitFilters} />
        </div>

        {/* Form L spotlight */}
        <div style={{ ...cardBox, background: `linear-gradient(135deg, #2A0F45 0%, #4A1D6E 100%)`, color: "#fff", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24, marginBottom: 20, borderLeft: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 26 }}>&#9792;</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "#E9D5FF" }}>Form L &mdash; POSH / Night-shift Consent</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.6)" }}>Statutory consent required for female new hires</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 28, marginLeft: "auto", flexWrap: "wrap" }}>
            {[["Female hires", summary.femaleTotal, "#fff"], ["Consent given", summary.formLCompleted, "#34EAA6"], ["Pending", summary.formLPending, "#FCD34D"]].map(([l, v, col]) => (
              <div key={l as string}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>{l}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: col as string }}>{v as number}</div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.55)" }}>Consent rate</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#E9D5FF" }}>
                {summary.femaleTotal ? Math.round((summary.formLCompleted / summary.femaleTotal) * 100) : 0}%
              </div>
            </div>
            <Button label="View pending" icon="pi pi-filter" className="p-button-sm" onClick={() => setFormLPendingOnly(true)} />
          </div>
        </div>

        {/* KPI row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 14, marginBottom: 14 }}>
          {kpi("Total Enrolled", summary.total, C.cyan2, "India new hires (cohort)")}
          {kpi("Completed", summary.completed, C.green, "All forms submitted")}
          {kpi("In Progress", summary.inProgress, C.amber, "Partially submitted")}
          {kpi("Overdue", summary.overdue, C.rose, "Past due date")}
          {kpi("Not Started", summary.notStarted, C.navy, "Scheduled, yet to begin")}
          {kpi("Compliance Rate", `${summary.pct}%`, C.cyan2, `${summary.completed}/${summary.total} compliant`)}
        </div>

        {/* Charts */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 14, marginBottom: 20 }}>
          <div style={{ ...cardBox }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Overall Compliance</div>
            <div style={{ position: "relative", height: 220 }}>
              <Chart type="doughnut" data={complianceChartData} options={ringOptions} style={{ height: "100%" }} />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: C.text }}>{summary.pct}%</div>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em" }}>Compliant</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{summary.completed}/{summary.total}</div>
              </div>
            </div>
          </div>
          <div style={{ ...cardBox }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>Status Breakdown</div>
            <div style={{ height: 220 }}>
              {summary.total > 0
                ? <Chart type="doughnut" data={statusChartData} options={donutOptions} style={{ height: "100%" }} />
                : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 13 }}>No data yet</div>}
            </div>
          </div>
          <div style={{ ...cardBox }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Needs Attention</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { label: "Overdue", val: summary.overdue, col: C.rose, action: () => { setStatus("Overdue"); setPage(1); setSubmitCount((c) => c + 1); } },
                { label: "In Progress", val: summary.inProgress, col: C.amber, action: () => { setStatus("In Progress"); setPage(1); setSubmitCount((c) => c + 1); } },
                { label: "Not Started", val: summary.notStarted, col: C.navy, action: () => { setStatus("Not Started"); setPage(1); setSubmitCount((c) => c + 1); } },
                { label: "Form L pending", val: summary.formLPending, col: "#9b59b6", action: () => { setFormLPendingOnly(true); setPage(1); } },
              ].map((a) => (
                <div key={a.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: "#F8FAFF", borderRadius: 8 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.col as string }} />
                    {a.label}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: a.col as string }}>{a.val ?? 0}</span>
                    <Button label="View" className="p-button-text p-button-sm" onClick={a.action} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: C.muted }}>{selected.length}/5 selected</span>
          <Button label="Download PDFs" icon="pi pi-download" className="p-button-sm" disabled={selected.length === 0 || bulkDownloading} loading={bulkDownloading} onClick={downloadBulk} />
        </div>

        {/* Table */}
        <div className="icd-tablewrap" style={{ ...cardBox, padding: 0, overflow: "hidden" }}>
          <style>{`
            .icd-tablewrap .p-datatable-tbody > tr > td { white-space: nowrap; }
            .icd-tablewrap .p-datatable-tbody > tr > td.icd-program { white-space: normal; line-height: 1.35; }
            .icd-tablewrap .p-datatable-thead > tr > th:first-child,
            .icd-tablewrap .p-datatable-tbody > tr > td:first-child {
              position: sticky; left: 0; z-index: 1; background: #fff;
              box-shadow: 2px 0 5px rgba(1,42,87,.06);
            }
            .icd-tablewrap .p-datatable-thead > tr > th:first-child { z-index: 3; background: #F4F6FA; }
          `}</style>
          <DataTable
            value={displayRows}
            lazy paginator
            first={(page - 1) * rows}
            rows={rows}
            totalRecords={totalRows}
            rowsPerPageOptions={[15, 25, 50]}
            onPage={onPage}
            onSort={onSort}
            sortField={sortBy}
            sortOrder={sortDir === "asc" ? 1 : -1}
            dataKey="oracleId"
            selection={selected}
            onSelectionChange={(e: any) => { const v = e.value as Row[]; if (v.length > 5) { toast.current?.show({ severity: "warn", summary: "Limit", detail: "You can download up to 5 PDFs at once.", life: 2500 }); return; } setSelected(v); }}
            isDataSelectable={(e: any) => e?.data?.completionPct === 100}
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={rowExpansionTemplate}
            responsiveLayout="scroll"
            tableStyle={{ minWidth: "100%" }}
            emptyMessage="No employees match these filters."
            paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column expander style={{ width: "3rem" }} />
            <Column field="name" header="Employee" body={nameTpl} sortable style={{ minWidth: 160 }} />
            <Column field="oracleId" header="Oracle ID" sortable style={{ minWidth: 110 }} />
            <Column field="startDate" header="Start Date" sortable style={{ minWidth: 120 }} />
            <Column field="dueDate" header="Due Date" sortable style={{ minWidth: 120 }} />
            <Column field="completedOn" header="Completed" body={(r: Row) => r.completedOn || "\u2014"} sortable style={{ minWidth: 120 }} />
            <Column field="overall" header="Status" body={statusTpl} sortable style={{ minWidth: 120 }} />
            <Column header="Action" body={actionTpl} style={{ minWidth: 130 }} />
          </DataTable>
        </div>
      </div>
    </div>
  );
}

/* inline style objects */
const cardBox: React.CSSProperties = { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 2px 16px rgba(1,42,87,.08)", padding: "16px 20px" };
const kLabel: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".07em", color: C.muted, marginBottom: 6 };
const kValue: React.CSSProperties = { fontSize: 30, fontWeight: 800, lineHeight: 1 };


